import {difference, get, isFinite, uniqBy} from "lodash";
import "./resource";
import { thor } from "../../typings/thor";
import { SaleTranslations } from "../../typings/translations";

export class SaleService {
    public static $inject: string[] = [
        "$log",
        "$q",
        "$translate",
        "authService",
        "configService",
        "saleResource",
        "saleTranslationsResource",
        "sitesService",
        "utilsService"
    ];

    public itemsResources = [];

    private paginationItems: number;

    constructor(
        protected $log: ng.ILogService,
        protected $q: ng.IQService,
        private $translate: ng.translate.ITranslateService,
        private authService,
        configService,
        protected saleResource,
        private saleTranslationsResource,
        private sitesService,
        private utilsService
    ) {
        this.paginationItems = configService.pagination.default;
    }

    public cancelItemsQueriesIfRunning() {
        if (this.itemsResources.length > 0) {
            this.itemsResources
                .map(resource => resource.$cancelRequest());

            this.itemsResources = [];
        }
    }

    public getSale(saleId) {
        return this.saleResource()
            .get({ id: saleId })
            .$promise
            .catch(error => {
                this.$log.error(`Error while fetching sale ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public getItemsCount(saleId, suppressed = false) {
        return this.saleResource().countItems({ id: saleId, suppressed }).$promise;
    }

    public getItems(saleId, suppressed = false) {
        this.itemsResources = [];

        return this.saleResource()
            .countItems({ id: saleId })
            .$promise.then(({ count }) => {
                const apiCalls = Math.ceil(count / this.paginationItems);

                return this.$q.series(
                    this.utilsService.range(0, apiCalls - 1),
                    this.getItemsPart.bind(this, saleId, suppressed)
                );
            })
            .then((values: any[]) => {
                this.itemsResources = [];

                const items = values.reduce((a: any[], b: any[]) => a.concat(b), []);
                const itemsNoDuplication = uniqBy(items, "id");
                const diff = difference(items, itemsNoDuplication);

                if (diff.length > 0) {
                    this.$log.error("Duplication found in list", diff);
                }
                return this.$q.resolve(itemsNoDuplication);
            })
            .catch(error => {
                this.$log.error(`Error while fetching items of sale ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public getItemsPart(saleId, suppressed = false, numCall) {
        const queryParams: any = {
            id: saleId,
            suppressed
        };
        const itemsResource = this.saleResource(
            { "X-Range": this.utilsService.getContentRange(numCall, this.paginationItems) }
        ).getItems(queryParams);
        this.itemsResources.push(itemsResource);

        return itemsResource.$promise;
    }

    public updateSaleStates(sale: thor.SaleGet, states) {
        if (!states.states.deleted &&
            !states.states.hidden &&
            !this.checkIfEveryMandatoryFieldsAreFilled(sale)
            ) {
                const toastMessage = this.$translate.instant(
                    states.states.canceled
                        ? "annulation_impossible_l_un_des_champs_obligatoires_de_cette_annonce_n_est_pas_complete"
                        : "mise_en_ligne_impossible_l_un_des_champs_obligatoires_de_cette_annonce_n_est_pas_complete"
                );
                return this.$q.reject({ toastMessage });
        }

        return this.setStates(sale.id, states)
            .then(() => {
                return this.publish(sale.id);
            })
            .catch(error => {
                return this.$q.reject(error);
            });
    }

    public setMediaResults(saleId, mediaId) {
        return this.saleResource()
            .setMediaResults({ id: saleId }, { id: mediaId })
            .$promise
            .catch(error => {
                this.$log.error(`An error occurred while setting sale's results pdf on ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public deleteMediaResults(saleId) {
        return this.saleResource()
            .deleteMediaResults({ id: saleId })
            .$promise
            .catch(error => {
                this.$log.error(`An error occurred while removing sale's results pdf on ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public setMediaCatalog(saleId, mediaId) {
        return this.saleResource()
            .setMediaCatalog({ id: saleId }, { id: mediaId })
            .$promise
            .catch(error => {
                this.$log.error(`An error occurred while setting sale's catalog pdf on ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public deleteMediaCatalog(saleId) {
        return this.saleResource()
            .deleteMediaCatalog({ id: saleId })
            .$promise
            .catch(error => {
                this.$log.error(`An error occurred while removing sale's catalog pdf ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public updateSaleConditions(saleId, originalSaleConditionsId, newSaleConditionsId) {
        if (originalSaleConditionsId) {
            return this.deleteSaleConditions(saleId)
                .catch(error => {
                    this.$log.error("try to delete sale's conditions", error);
                })
                .then(() => {
                    return this.setSaleConditions(saleId, newSaleConditionsId);
                });
        } else {
            return this.setSaleConditions(saleId, newSaleConditionsId);
        }
    }

    public setSaleConditions(saleId, saleConditionsId) {
        return this.saleResource()
            .setSaleConditions({ id: saleId }, { id: saleConditionsId })
            .$promise
            .catch(error => {
                this.$log.error(`An error occurred while setting sale's conditions ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public deleteSaleConditions(saleId) {
        return this.saleResource()
            .deleteSaleConditions({ id: saleId })
            .$promise
            .catch(error => {
                this.$log.error(`An error occurred while removing sale's conditions ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public update(saleId, data) {
        return this.saleResource()
            .update({ id: saleId }, data)
            .$promise
            .catch(error => {
                this.$log.error(`Error while updating the sale ${saleId}`, error, data);
                return this.$q.reject(error);
            });
    }

    public create(object) {
        return this.saleResource()
            .save(object)
            .$promise
            .catch(error => {
                this.$log.error("Error while creating sale", error);
                return this.$q.reject(error);
            });
    }

    public createItem(saleId, itemPost) {
        return this.saleResource()
            .createItem(
                { id: saleId },
                itemPost
            )
            .$promise
            .catch(error => {
                this.$log.error(`Error while creating new item on sale ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    public delete(id: number) {
        return this.saleResource()
            .delete({ id: id })
            .$promise
            .catch(error => {
                this.$log.error(`Error while deleting sale ${id}`, error);
                return this.$q.reject(error);
            });
    }

    public reset(id: number) {
        return this.saleResource()
            .reset({ id: id })
            .$promise
            .catch(error => {
                this.$log.error(`Error while reset new item on sale ${id}`, error);
                return this.$q.reject(error);
            });
    }

    public getSaleTranslationStatus(id: number) {
        return this.saleTranslationsResource()
            .getSaleStatus({ id })
            .$promise.catch(error => {
                this.$log.error(`Error on get sale status ${id}`, error);
                return this.$q.reject(error);
            });
    }

    public translate(id: number, body: SaleTranslations) {
        return this.saleTranslationsResource()
            .translate({ id }, JSON.stringify(body))
            .$promise.catch(error => {
                this.$log.error(`Error on sale translations ${id}`, error);
                return this.$q.reject(error);
            });
    }

    public getPublished(id: number) {
        return this.saleResource()
            .getPublished({ id })
            .$promise.catch(error => {
                this.$log.error(`Error on get published sale ${id}`, error);
                return this.$q.reject(error);
            });
    }

    protected checkIfEveryMandatoryFieldsAreFilled(sale: thor.SaleGet): boolean {
        return Boolean(
            sale.name &&
            sale.datetime &&
            (sale.category_id || sale.category.id) &&
            sale.description &&
            sale.sale_conditions &&
            this.isCommissionRateFilled(sale) &&
            this.isShippingFilled(sale)
        );
    }

    protected isCommissionRateFilled(sale: thor.SaleGet): boolean {
        return (
            isFinite(get(sale, "options.commission_rate.voluntary")) ||
            isFinite(get(sale, "options.commission_rate.judicial"))
        );
    }

    protected isShippingFilled(sale: thor.SaleGet): boolean {
        return Boolean(
            this.sitesService.isAuction(sale.sites) ||
            !this.utilsService.isShippingRequired(this.authService.organization, sale) ||
            this.utilsService.isShippingFilled(sale.shipping)
        );
    }

    private setStates(saleId, states) {
        return this.saleResource()
            .update(
                { id: saleId },
                states
            )
            .$promise
            .catch(error => {
                this.$log.error(`Error while updating the sale ${saleId}`, error);
                return this.$q.reject(error);
            });
    }

    private publish(saleId) {
        return this.saleResource().publish({ id: saleId }).$promise;
    }
}

angular
    .module("thor.core.sale.service", [
        "thor.core.sale.resource"
    ])
    .service("saleService", SaleService);
